import { useEffect, useMemo, type CSSProperties } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '@/app/providers/authContext';
import { NotificationBell } from '@/features/inactivity/NotificationBell';
import { useCurrentEstablishment } from '@/features/organization';

import { AccountMenu } from './AccountMenu';
import { CartBadge } from './CartBadge';
import { NavIcon } from './NavIcon';
import { navigationLayoutForProfile } from './navigation';
import { useSlidingIndicator } from '@/lib/hooks';
import styles from './Topbar.module.css';

export interface TopbarProps {
  /** Opens the "Ir para" palette (⌘K). Without it the bar draws no search button. */
  readonly onSearch?: (() => void) | undefined;
}

const SHORTCUT =
  typeof navigator !== 'undefined' && /mac/iu.test(navigator.platform) ? '⌘K' : 'Ctrl K';

/**
 * The navigation of every profile on every screen size. Daily destinations sit in a page switcher
 * at the centre of the bar (for a customer: Comprar and Pedidos; for the back office: the modules
 * plus Backoffice, whose own sections live in a sidebar inside it); account-level pages sit in the
 * account menu. The split is `navigationLayoutForProfile`; the route guards never read it.
 *
 * On a desktop the current destination is one pill that slides between the others. On a phone the
 * destinations drop to a row of their own (a segmented control for up to three).
 *
 * Neither the tenant nor the profile is ever selectable — both are fixed by the claims in the
 * access token. The establishment is not selectable either, yet, and the topbar now says which
 * one is in force rather than advertising a picker: see `useCurrentEstablishment`.
 */
export function Topbar({ onSearch }: TopbarProps = {}) {
  const { profile } = useAuth();
  const { establishmentName, establishmentCount } = useCurrentEstablishment();
  const layout = useMemo(() => navigationLayoutForProfile(profile), [profile]);
  const { pathname } = useLocation();
  // Which inline destination the current route belongs to — -1 on a route that is none of them
  // (an account page), where nothing is lit. The cart counts as buying: "Finalizar" is the end of
  // Comprar, and a bar with no destination lit there read as being nowhere.
  const journeyPath = pathname.startsWith('/carrinho') ? '/catalogo' : pathname;
  const activeIndex = layout.primary.findIndex(
    (item) => journeyPath === item.to || journeyPath.startsWith(`${item.to}/`),
  );

  const navRef = useSlidingIndicator<HTMLElement>(`${String(profile)}:${String(activeIndex)}`);

  // With more destinations than fit a phone they scroll sideways, and the current one could sit
  // past the edge — "Backoffice" for an administrator. Bring it into view whenever it changes.
  useEffect(() => {
    const active = navRef.current?.querySelector<HTMLElement>('[data-current]');
    if (active !== null && active !== undefined && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [activeIndex, navRef]);

  return (
    <header className={styles.topbar} data-topbar="">
      <Link to="/" className={styles.brand} viewTransition>
        <span className={styles.mark} aria-hidden="true">
          In
        </span>
        <span className={styles.hideOnMobile}>Instoque</span>
      </Link>

      {layout.primary.length > 0 ? (
        <nav
          ref={navRef}
          className={styles.nav}
          aria-label="Navegação principal"
          // Up to three destinations become a segmented control on a phone; more stay a row of
          // pills that scrolls, since a segment narrower than its label is unreadable.
          data-segmented={layout.primary.length <= 3 ? '' : undefined}
          data-has-active={activeIndex >= 0 ? '' : undefined}
          style={
            {
              '--segments': layout.primary.length,
              '--active': Math.max(activeIndex, 0),
            } as CSSProperties
          }
        >
          <span className={styles.navIndicator} aria-hidden="true" />
          {layout.primary.map((item, index) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              viewTransition
              data-current={index === activeIndex ? '' : undefined}
              className={[styles.navLink, index === activeIndex ? styles.navActive : null]
                .filter(Boolean)
                .join(' ')}
            >
              <NavIcon name={item.to} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      ) : (
        <p className={styles.noProfile}>Seu usuário ainda não tem um perfil de acesso definido.</p>
      )}

      <div className={styles.spacer} />

      <div className={styles.tools}>
        {/*
        A disabled select reading "Estabelecimento (em breve)" sat here from Sprint 1 until the
        v1.0.0 review: a control nobody can operate, announcing a feature by no particular date,
        shipped in a release called 1.0. What a user actually needs to know is which unit the
        purchasing screens are acting for, and that is a fact this application has. When a customer
        has more than one, the label says so — silently scoping to the first of several without
        telling anyone would be the same mistake in a quieter form.
      */}
        {establishmentName != null ? (
          <p className={styles.scope} title={establishmentName}>
            <span className={styles.scopeLabel}>Estabelecimento</span>
            <span className={styles.scopeValue}>{establishmentName}</span>
            {establishmentCount > 1 ? (
              <span className={styles.scopeNote}>
                {`1 de ${String(establishmentCount)} — pedidos são feitos por esta unidade`}
              </span>
            ) : null}
          </p>
        ) : null}

        {onSearch === undefined ? null : (
          <button
            type="button"
            className={styles.search}
            onClick={onSearch}
            aria-label={`Buscar uma página ou seção (${SHORTCUT})`}
            data-tip={`Buscar · ${SHORTCUT}`}
          >
            <NavIcon name="search" />
          </button>
        )}

        {/* The bell sits before the cart: news about whether the unit can still buy comes
            before what is waiting to be bought. Its list and the warning on entry hang from the
            bar's right edge (Figma I8, I9). */}
        <NotificationBell />

        <CartBadge />

        <div className={styles.userMenu}>
          <AccountMenu variant="bar" items={layout.menu} />
        </div>
      </div>
    </header>
  );
}
